import {
  NavigationInstrumentation,
  getWebInstrumentations,
  initializeFaro,
} from '@grafana/faro-web-sdk';
import {
  TracingInstrumentation,
  getDefaultOTELInstrumentations,
} from '@grafana/faro-web-tracing';

import { isMcFaroProdCookieEnabled } from './faro-prod-cookie';

export interface InitFaroOptions {
  appName?: string;
  collectorUrl: string;
  enabled: boolean;
  environment: string;
  version: string;
  resourceAttributes: Record<string, string>;
  /**
   * Query params to strip from `event.meta.page.url` before telemetry is sent, mirroring
   * Pendo's `ExcludeKeys` location transform for sensitive values that must stay in the
   * URL (e.g. `ru`) but must not leak into analytics.
   */
  excludeUrlParams?: string[];
}

export interface InitFaroEnvironment {
  location: Pick<Location, 'hostname' | 'port'>;
  userAgent: string;
}

function getDefaultEnvironment(): InitFaroEnvironment {
  return {
    location: window.location,
    userAgent: window.navigator.userAgent,
  };
}

export function isFaroDisabledOnLocalDev(
  location: Pick<Location, 'hostname' | 'port'> = window.location
): boolean {
  return (
    location.hostname === 'localhost' ||
    location.port === '4200' ||
    location.port === '4202'
  );
}

export function initFaro(
  options: InitFaroOptions,
  environment: InitFaroEnvironment = getDefaultEnvironment()
): void {
  if (
    !options.enabled ||
    !options.collectorUrl ||
    environment.userAgent.toLowerCase().includes('cypress') ||
    isFaroDisabledOnLocalDev(environment.location) ||
    (options.environment === 'prod' && !isMcFaroProdCookieEnabled())
  ) {
    return;
  }

  // Nest resource attrs under app so Loki flattens them as app_region / app_cluster.
  // Do not pass a custom `metas` array - that replaces Faro's defaults (including page meta).
  const appInfo = {
    name: options.appName,
    version: options.version,
    environment: options.environment,
    bundleId: window.__faroBundleId__ ?? '',
    ...options.resourceAttributes,
  };

  initializeFaro({
    url: options.collectorUrl,
    app: appInfo,
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation({
        instrumentations: [
          ...getDefaultOTELInstrumentations({
            ignoreUrls: [/\/collector\//],
          }),
        ],
        resourceAttributes: options.resourceAttributes,
      }),
      new NavigationInstrumentation(),
    ],
    sessionTracking: {
      enabled: true,
      persistent: true,
      session: {
        attributes: {
          region: options.resourceAttributes['region'],
          cluster: options.resourceAttributes['cluster'],
        },
      },
    },
    batching: {
      enabled: true,
      sendTimeout: 5000, // Send every 5 seconds
    },
    preserveOriginalError: true,
    beforeSend(event) {
      const http = (event as any)?.context?.http;
      if (http?.headers) {
        delete http.headers.authorization;
        delete http.headers.cookie;
      }

      if (options.excludeUrlParams?.length) {
        stripUrlParams(event?.meta?.page, options.excludeUrlParams);
      }

      return event;
    },
  });
}

/**
 * Strips `paramsToExclude` from `page.url`'s query string. The path and hash are
 * sliced out manually (rather than parsed via the `URL` API) so this works for
 * absolute URLs, root-relative URLs (e.g. `/login?ru=...`), and values without a
 * query string alike - without ever throwing on a malformed value. The query string
 * itself is parsed and rewritten via `URLSearchParams`.
 */
function stripUrlParams(
  page: { url?: string } | undefined,
  paramsToExclude: string[]
): void {
  if (!page?.url) {
    return;
  }

  const hashIndex = page.url.indexOf('#');
  const beforeHash = hashIndex === -1 ? page.url : page.url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : page.url.slice(hashIndex);

  const queryIndex = beforeHash.indexOf('?');
  if (queryIndex === -1) {
    return;
  }

  const base = beforeHash.slice(0, queryIndex);
  const params = new URLSearchParams(beforeHash.slice(queryIndex + 1));

  if (!paramsToExclude.some((param) => params.has(param))) {
    return;
  }

  paramsToExclude.forEach((param) => params.delete(param));
  const query = params.toString();
  const queryString = query ? `?${query}` : '';

  page.url = `${base}${queryString}${hash}`;
}

declare global {
  interface Window {
    __faroBundleId__?: string;
  }
}
